import React, { Suspense, lazy } from 'react';
import DiagramTile from './DiagramTile';
import EmojiDiagramView from './EmojiDiagramView';
import { emojiRowsToGrid } from '../../utils/diagramEditor';

const DiagramVisualEditor = lazy(() => import('./DiagramVisualEditor'));

const EditDiagramModal = ({
  diagramEditor,
  setDiagramEditor,
  diagramEditorError,
  isSavingDiagram,
  onClose,
  onRegenerateSvg,
  onSaveDiagram,
  onOpenExcalidraw,
  svgPreviewDoc,
  // tikzPreviewDoc is no longer needed; TikZ live preview now goes through
  // DiagramTile which loads TikZJax on demand and writes back the rendered
  // SVG. Caller still passes it (from useDiagramEditor) — we just ignore it.
}) => {
  if (!diagramEditor?.isOpen) return null;

  const emojiRows = diagramEditor.emojiRows || [];
  const isEmojiMode = diagramEditor.editorMode === 'emoji';
  const emojiPreviewGrid = emojiRowsToGrid(emojiRows);

  const updateEmojiRows = (updater) =>
    setDiagramEditor((prev) => ({ ...prev, emojiRows: updater(prev.emojiRows || []) }));

  const updateEmojiRow = (rowIndex, field, value) =>
    updateEmojiRows((rows) => rows.map((row, idx) => (idx === rowIndex ? { ...row, [field]: value } : row)));

  // +/- adjusts the count by repeating (or dropping) the row's last item.
  const changeEmojiCount = (rowIndex, delta) =>
    updateEmojiRows((rows) => rows.map((row, idx) => {
      if (idx !== rowIndex) return row;
      const items = (row.items || '').split(/\s+/).filter(Boolean);
      if (delta > 0) items.push(items[items.length - 1] || '⭐');
      else items.pop();
      return { ...row, items: items.join(' ') };
    }));

  // Prefer the already-rendered SVG (svg_cache) in the preview so opening the
  // editor on a cached diagram doesn't recompile TikZ. Only fall back to live
  // TikZ compilation when the source has been edited (so the cached SVG is now
  // stale) or there is no rendered SVG to show yet.
  const isTikzEdited = diagramEditor.tikzCode !== diagramEditor.originalTikzCode;
  const showTikzLivePreview =
    diagramEditor.editorMode === 'tikz' && (isTikzEdited || !diagramEditor.svgCode);

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Edit Diagram</h2>
            <p className="text-sm text-gray-600 mt-1">Question {diagramEditor.questionNumber || '-'}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl sm:text-3xl font-bold w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full hover:bg-gray-200 transition-all"
            aria-label="Close diagram editor"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setDiagramEditor((prev) => ({ ...prev, editorMode: 'emoji' }))}
                className={`px-2 py-1.5 text-xs sm:text-sm rounded-full ${isEmojiMode ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
              >
                Emoji
              </button>
              <button
                type="button"
                onClick={() => setDiagramEditor((prev) => ({ ...prev, editorMode: 'visual' }))}
                className={`px-2 py-1.5 text-xs sm:text-sm rounded-full ${diagramEditor.editorMode === 'visual' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
              >
                Visual
              </button>
              <button
                type="button"
                onClick={() => setDiagramEditor((prev) => ({ ...prev, editorMode: 'svg' }))}
                className={`px-2 py-1.5 text-xs sm:text-sm rounded-full ${diagramEditor.editorMode === 'svg' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
              >
                SVG
              </button>
              <button
                type="button"
                onClick={() => setDiagramEditor((prev) => ({ ...prev, editorMode: 'tikz' }))}
                className={`px-2 py-1.5 text-xs sm:text-sm rounded-full ${diagramEditor.editorMode === 'tikz' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
              >
                TikZ
              </button>
              {onOpenExcalidraw && (
                <button
                  type="button"
                  onClick={onOpenExcalidraw}
                  className="px-1 py-1.5 text-xs sm:text-sm rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 border border-violet-200"
                >
                  ✏ Sketch Diagram
                </button>
              )}
            </div>

            {isEmojiMode ? (
              <div className="space-y-3">
                <p className="text-xs text-gray-500">
                  One row per line of the picture chart. Separate emojis with spaces; leave the label empty for rows without one.
                </p>
                {emojiRows.map((row, rowIndex) => (
                  <div key={rowIndex} className="flex flex-wrap sm:flex-nowrap items-center gap-2">
                    <input
                      type="text"
                      value={row.label}
                      onChange={(event) => updateEmojiRow(rowIndex, 'label', event.target.value)}
                      className="w-full sm:w-32 p-2 border border-gray-300 rounded-md text-sm"
                      placeholder="Label (e.g. Cars:)"
                    />
                    <input
                      type="text"
                      value={row.items}
                      onChange={(event) => updateEmojiRow(rowIndex, 'items', event.target.value)}
                      className="flex-1 min-w-0 p-2 border border-gray-300 rounded-md text-lg"
                      placeholder="⭐ ⭐ ⭐"
                    />
                    <button
                      type="button"
                      onClick={() => changeEmojiCount(rowIndex, -1)}
                      className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                      aria-label={`Remove one item from row ${rowIndex + 1}`}
                    >
                      −
                    </button>
                    <button
                      type="button"
                      onClick={() => changeEmojiCount(rowIndex, 1)}
                      className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                      aria-label={`Add one item to row ${rowIndex + 1}`}
                    >
                      +
                    </button>
                    <button
                      type="button"
                      onClick={() => updateEmojiRows((rows) => rows.filter((_, idx) => idx !== rowIndex))}
                      className="px-2 py-1 text-sm text-red-600 border border-red-200 rounded-md hover:bg-red-50"
                      aria-label={`Delete row ${rowIndex + 1}`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => updateEmojiRows((rows) => [...rows, { label: '', items: '' }])}
                  className="px-3 py-2 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-md"
                >
                  + Add Row
                </button>
              </div>
            ) : diagramEditor.editorMode === 'visual' ? (
              <Suspense fallback={<div className="text-sm text-gray-500">Loading visual editor…</div>}>
                <DiagramVisualEditor
                  key={`${diagramEditor.questionIndex || 'diagram'}-${diagramEditor.isOpen ? 'open' : 'closed'}`}
                  svgCode={diagramEditor.svgCode}
                  onSvgCodeChange={(newSvgCode) => {
                    setDiagramEditor((prev) => ({ ...prev, svgCode: newSvgCode }));
                  }}
                />
              </Suspense>
            ) : diagramEditor.editorMode === 'svg' ? (
              <textarea
                value={diagramEditor.svgCode}
                onChange={(event) => setDiagramEditor((prev) => ({ ...prev, svgCode: event.target.value }))}
                className="w-full min-h-[20rem] p-3 border border-gray-300 rounded-md font-mono text-xs"
                placeholder="<svg>...</svg>"
              />
            ) : (
              <div className="space-y-3">
                <textarea
                  value={diagramEditor.tikzCode}
                  onChange={(event) => setDiagramEditor((prev) => ({ ...prev, tikzCode: event.target.value }))}
                  className="w-full min-h-[20rem] p-3 border border-gray-300 rounded-md font-mono text-xs"
                  placeholder="\\begin{tikzpicture} ... \\end{tikzpicture}"
                />
                <button
                  type="button"
                  onClick={onRegenerateSvg}
                  className="px-3 py-2 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-md"
                >
                  Regenerate SVG Preview
                </button>
              </div>
            )}

            {diagramEditorError && (
              <div className="text-sm text-red-600">{diagramEditorError}</div>
            )}
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700">Preview</h3>
            {isEmojiMode ? (
              emojiPreviewGrid.length > 0 ? (
                <div className="w-full h-[26rem] overflow-auto border border-gray-200 rounded-md bg-gray-50 p-3 flex items-center justify-center">
                  <EmojiDiagramView
                    diagram={{ ...(diagramEditor.emojiBaseDiagram || {}), type: 'emoji', labels: emojiPreviewGrid }}
                  />
                </div>
              ) : (
                <div className="w-full h-[26rem] border border-dashed border-gray-300 rounded-md bg-gray-50 flex items-center justify-center text-sm text-gray-500 px-6 text-center">
                  Add a row to preview the emoji diagram.
                </div>
              )
            ) : showTikzLivePreview ? (
              <div className="w-full h-[26rem] overflow-auto border border-gray-200 rounded-md bg-gray-50 p-3 flex items-center justify-center">
                <DiagramTile
                  key={`tikz-preview-${diagramEditor.questionIndex ?? 'none'}-${diagramEditor.tikzCode || ''}`}
                  diagram={{ type: 'tikz', code: diagramEditor.tikzCode || '' }}
                  autoRender
                  className="max-w-full"
                />
              </div>
            ) : diagramEditor.svgCode ? (
              <iframe
                key={`svg-preview-${diagramEditor.questionIndex ?? 'none'}-${diagramEditor.svgCode || ''}`}
                title="diagram-svg-preview"
                srcDoc={svgPreviewDoc}
                className="w-full h-[26rem] border border-gray-200 rounded-md bg-gray-50"
              />
            ) : (
              <div className="w-full h-[26rem] border border-dashed border-gray-300 rounded-md bg-gray-50 flex items-center justify-center text-sm text-gray-500 px-6 text-center">
                Preview will appear here.
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md"
            type="button"
          >
            Cancel
          </button>
          <button
            onClick={onSaveDiagram}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md disabled:opacity-50"
            type="button"
            disabled={isSavingDiagram}
          >
            {isSavingDiagram
              ? 'Saving...'
              : ({ tikz: 'Save TikZ', emoji: 'Save Emoji Diagram' }[diagramEditor.editorMode] || 'Save SVG')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditDiagramModal;
